import { isNotEmpty } from '@lhb/func';
import dayjs from 'dayjs';
import type { LocxxDirectTaskCreateParams } from '@/common/api/expandStore/expansiontask';

export interface LocxxDirectTaskCreateValues {
  modelClusterId?: number;
  circleBusinessId?: number;
  businessName?: string;
  targetAddress?: number[];
  assignAccountIds?: number[];
  managerId?: number;
  marketingPurpose?: 6 | 7 | 8;
  areaRequirementMin?: number;
  areaRequirementMax?: number;
  floorHeight?: number;
  minFlowBottomLine?: number;
  activityPeriodCount?: number;
  activityPeriodUnit?: 1 | 2 | 3;
  promotionDate?: string[];
  totalBudget?: number;
  expectDropInDate?: string;
}

export interface LocxxTaskValidationResult {
  valid: boolean;
  message?: string;
}

export interface LocxxMapBusinessInfo {
  id?: number;
  areaName?: string;
  provinceId?: number;
  cityId?: number;
  districtId?: number;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
}

export interface LocxxDirectTaskUpdateValues {
  id: number;
  name?: string;
  marketingPurpose?: 6 | 7 | 8;
  expectDropInDate?: string;
  areaRequirementMin?: number;
  areaRequirementMax?: number;
  floorHeight?: number;
  minFlowBottomLine?: number;
  activityPeriodCount?: number;
  activityPeriodUnit?: 1 | 2 | 3;
  promotionDateStart?: string;
  promotionDateEnd?: string;
  totalBudget?: number;
}

export function buildLocxxTaskName(cityName?: string, businessName?: string) {
  return cityName && businessName ? `${cityName}_${businessName}_拓店任务` : '';
}

/** 将选址地图商圈详情转换为 Locxx 创建表单的只读初始值。 */
export function buildLocxxMapBusinessInitialValues(businessInfo: LocxxMapBusinessInfo) {
  const {
    id,
    areaName,
    provinceId,
    cityId,
    districtId,
    provinceName,
    cityName,
    districtName,
  } = businessInfo;
  return {
    businessName: areaName || '',
    targetAddress: [provinceId, cityId, districtId].filter((item) => isNotEmpty(item)),
    targetAddressInfo: [provinceName, cityName, districtName].filter((item) => isNotEmpty(item)),
    circleBusinessId: id,
    name: buildLocxxTaskName(cityName, areaName),
  };
}

export function validateLocxxTaskCreateValues(values: LocxxDirectTaskCreateValues): LocxxTaskValidationResult {
  if (!values.businessName) {
    return { valid: false, message: '请输入拓店任务名称' };
  }
  if (!values.totalBudget && values.totalBudget !== 0) {
    return { valid: false, message: '请输入总预算' };
  }
  if (!values.expectDropInDate) {
    return { valid: false, message: '请选择反馈截止日期' };
  }
  if (isNotEmpty(values.areaRequirementMin) && isNotEmpty(values.areaRequirementMax) && Number(values.areaRequirementMin) > Number(values.areaRequirementMax)) {
    return { valid: false, message: '面积范围填写有误，最小面积不能大于最大面积' };
  }
  const promotionDate = values.promotionDate || [];
  if (promotionDate[0] && promotionDate[1] && dayjs(promotionDate[0]).isAfter(dayjs(promotionDate[1]), 'day')) {
    return { valid: false, message: '活动开始日期不能晚于结束日期' };
  }
  return { valid: true };
}

export function buildLocxxDirectTaskCreateParams(
  values: LocxxDirectTaskCreateValues,
  cityName?: string,
): LocxxDirectTaskCreateParams {
  const targetAddress = values.targetAddress || [];
  const promotionDate = values.promotionDate || [];
  return {
    modelClusterId: Number(values.modelClusterId || values.circleBusinessId),
    name: buildLocxxTaskName(cityName, values.businessName),
    ...(targetAddress[0] ? { targetProvinceId: targetAddress[0] } : {}),
    ...(targetAddress[1] ? { targetCityId: targetAddress[1] } : {}),
    ...(targetAddress[2] ? { targetDistrictId: targetAddress[2] } : {}),
    ...(values.assignAccountIds?.length ? { assignAccountIds: values.assignAccountIds } : {}),
    ...(values.managerId ? { managerId: values.managerId } : {}),
    ...(values.marketingPurpose ? { marketingPurpose: values.marketingPurpose } : {}),
    ...(isNotEmpty(values.areaRequirementMin) ? { areaRequirementMin: values.areaRequirementMin } : {}),
    ...(isNotEmpty(values.areaRequirementMax) ? { areaRequirementMax: values.areaRequirementMax } : {}),
    ...(isNotEmpty(values.floorHeight) ? { floorHeight: values.floorHeight } : {}),
    ...(isNotEmpty(values.minFlowBottomLine) ? { minFlowBottomLine: values.minFlowBottomLine } : {}),
    ...(isNotEmpty(values.activityPeriodCount) ? { activityPeriodCount: values.activityPeriodCount } : {}),
    ...(values.activityPeriodUnit ? { activityPeriodUnit: values.activityPeriodUnit } : {}),
    ...(promotionDate[0] ? { promotionDateStart: promotionDate[0] } : {}),
    ...(promotionDate[1] ? { promotionDateEnd: promotionDate[1] } : {}),
    totalBudget: Number(values.totalBudget),
    expectDropInDate: values.expectDropInDate || '',
  };
}

/** 按 Locxx 直营任务详情可编辑字段组装更新请求。 */
export function buildLocxxDirectTaskUpdateParams(values: LocxxDirectTaskUpdateValues) {
  return {
    id: values.id,
    ...(values.name ? { name: values.name } : {}),
    ...(values.marketingPurpose ? { marketingPurpose: values.marketingPurpose } : {}),
    ...(values.expectDropInDate ? { expectDropInDate: values.expectDropInDate } : {}),
    ...(isNotEmpty(values.areaRequirementMin) ? { areaRequirementMin: values.areaRequirementMin } : {}),
    ...(isNotEmpty(values.areaRequirementMax) ? { areaRequirementMax: values.areaRequirementMax } : {}),
    ...(isNotEmpty(values.floorHeight) ? { floorHeight: values.floorHeight } : {}),
    ...(isNotEmpty(values.minFlowBottomLine) ? { minFlowBottomLine: values.minFlowBottomLine } : {}),
    ...(isNotEmpty(values.activityPeriodCount) ? { activityPeriodCount: values.activityPeriodCount } : {}),
    ...(values.activityPeriodUnit ? { activityPeriodUnit: values.activityPeriodUnit } : {}),
    ...(values.promotionDateStart ? { promotionDateStart: values.promotionDateStart } : {}),
    ...(values.promotionDateEnd ? { promotionDateEnd: values.promotionDateEnd } : {}),
    ...(isNotEmpty(values.totalBudget) ? { totalBudget: values.totalBudget } : {}),
  };
}
